// Resumen de cumplimiento documental de la compañía (GET /documents/dashboard):
// KPIs reales, faltantes críticos, próximos vencimientos y documentos por estado.
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Badge, Button, cn } from "../../../components/ui";
import { Kpi, LoadBlock, ProgressBar, Toolbar } from "../../bases-propiedades/components/console";
import { DOCUMENT_STATUSES, documentTypeName, type DocumentStatus } from "../../../utils/documentTypes";
import { isInCurrentMonth } from "../../../services/shipmentDocuments";
import { DOCS_NS, STATUS_TONES, completenessTone, expiryTone, shortDate } from "../../../features/shipmentDocuments/meta";
import { ExpiryChip } from "../../../features/shipmentDocuments/DocumentChecklist";
import { useCompanyDashboard, useCompanyList } from "../queries";
import { documentacionSubjectRoute } from "../nav";
import { KindBadge, queryStatus } from "../components/pieces";

const WINDOWS = [15, 30, 60, 90];

export default function ResumenSection() {
  const { t, i18n } = useTranslation(DOCS_NS);
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const dashboard = useCompanyDashboard(days);
  const d = dashboard.data;
  // «Generados del mes»: sólo se pide el repositorio si el tablero no trae `generated_this_month`.
  const generated = useCompanyList({ status: "generado", limit: 500 }, { enabled: Boolean(d) && typeof d?.generated_this_month !== "number" });
  const { status, error, retry } = queryStatus(dashboard);

  const generatedThisMonth = useMemo(() => {
    if (typeof d?.generated_this_month === "number") return d.generated_this_month;
    return (generated.data ?? []).filter((x) => isInCurrentMonth(x.updated_at)).length;
  }, [d, generated.data]);

  const statusRows = useMemo(() => {
    if (!d) return [];
    const total = Math.max(1, d.documents_total);
    return (DOCUMENT_STATUSES as readonly DocumentStatus[]).map((s) => ({ status: s, n: d.by_status?.[s] ?? 0, pct: Math.round(((d.by_status?.[s] ?? 0) / total) * 100) })).filter((r) => r.n > 0);
  }, [d]);

  const TONE_BG: Record<string, string> = { neutral: "bg-content-muted", primary: "bg-primary-500", success: "bg-success", warning: "bg-warning", error: "bg-error", info: "bg-info" };

  return (
    <div className="flex h-full w-full flex-col overflow-hidden">
      <Toolbar>
        <div className="flex items-center gap-2">
          <i className="ri-dashboard-3-line text-lg text-primary-500" />
          <h1 className="text-sm font-semibold text-content">{t("resumen.title")}</h1>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <label className="flex items-center gap-2 text-xs text-content-secondary">
            {t("resumen.window")}
            <select aria-label={t("resumen.window")} value={days} onChange={(e) => setDays(Number(e.target.value))} className="rounded-lg border border-line bg-app px-2 py-1.5 text-sm text-content outline-none focus:border-primary-500">
              {WINDOWS.map((w) => (
                <option key={w} value={w}>
                  {t("resumen.windowDays", { n: w })}
                </option>
              ))}
            </select>
          </label>
          <Button size="sm" variant="ghost" icon={<i className="ri-refresh-line" />} onClick={() => void dashboard.refetch()}>
            {t("common.refresh")}
          </Button>
        </div>
      </Toolbar>
      <div className="flex-1 overflow-y-auto p-4">
        <LoadBlock status={status} error={error} onRetry={retry} empty={!d || d.subjects_total === 0} emptyIcon="ri-folder-open-line" emptyTitle={t("resumen.emptyTitle")} emptyText={t("resumen.emptyText")}>
          {d ? (
            <div className="space-y-4" data-testid="docs-dashboard" data-tour="docs-dashboard">
              <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
                <Kpi icon="ri-checkbox-circle-line" label={t("resumen.kpi.complete")} value={d.subjects_complete} tone="success" progress={d.subjects_total ? (d.subjects_complete / d.subjects_total) * 100 : 0} hint={t("resumen.kpi.ofTotal", { n: d.subjects_total })} />
                <Kpi icon="ri-error-warning-line" label={t("resumen.kpi.incomplete")} value={d.subjects_incomplete} tone={d.subjects_incomplete ? "warning" : "success"} hint={t("resumen.kpi.pendingReview") + `: ${d.pending_review}`} />
                <Kpi icon="ri-time-line" label={t("resumen.kpi.expiring")} value={d.expiring} tone={d.expiring ? "warning" : "neutral"} hint={t("resumen.kpi.inWindow", { n: d.window_days })} />
                <Kpi icon="ri-calendar-close-line" label={t("resumen.kpi.expired")} value={d.expired} tone={d.expired ? "error" : "neutral"} hint={t("resumen.kpi.withExpired", { n: d.subjects_with_expired })} />
                <Kpi icon="ri-magic-line" label={t("resumen.kpi.generatedMonth")} value={generated.isLoading && typeof d.generated_this_month !== "number" ? "…" : generatedThisMonth} tone="primary" hint={t("resumen.kpi.byPlatform")} />
                <Kpi icon="ri-pie-chart-line" label={t("resumen.kpi.average")} value={`${d.average_completeness}%`} tone={completenessTone(d.average_completeness)} progress={d.average_completeness} hint={t("resumen.kpi.shipmentsBookings", { s: d.subjects_by_kind?.shipment ?? 0, b: d.subjects_by_kind?.booking ?? 0 })} />
              </div>

              <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
                <section className="rounded-xl border border-line bg-card p-4 xl:col-span-2" aria-labelledby="top-incomplete">
                  <div className="mb-3 flex items-center justify-between">
                    <h2 id="top-incomplete" className="text-sm font-semibold text-content">
                      {t("resumen.topIncomplete")}
                    </h2>
                    <span className="text-xs text-content-muted">{t("resumen.kpi.subjects", { n: d.subjects_incomplete })}</span>
                  </div>
                  {d.top_incomplete.length === 0 ? (
                    <p className="text-sm text-content-muted">{t("resumen.topIncompleteEmpty")}</p>
                  ) : (
                    <ul className="divide-y divide-line">
                      {d.top_incomplete.map((row) => (
                        <li key={`${row.subject.kind}:${row.subject.id}`}>
                          <button type="button" onClick={() => navigate(documentacionSubjectRoute(row.subject.kind, row.subject.id))} className="flex w-full items-center gap-3 py-2 text-left hover:bg-subtle">
                            <KindBadge kind={row.subject.kind} />
                            <div className="min-w-0 flex-1">
                              <div className="truncate font-mono text-xs text-content">{row.subject.id}</div>
                              <div className="truncate text-[11px] text-content-muted">{t("resumen.missingList", { list: row.missing.map((m) => documentTypeName(m, i18n.language)).join(", ") })}</div>
                            </div>
                            <div className="w-28 shrink-0">
                              <ProgressBar pct={row.percent} tone={completenessTone(row.percent)} />
                            </div>
                            <span className="w-10 shrink-0 text-right text-xs font-semibold tabular-nums text-content">{row.percent}%</span>
                            <Badge size="sm" tone="error">
                              {row.missing.length}
                            </Badge>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>

                <section className="rounded-xl border border-line bg-card p-4" aria-labelledby="by-status">
                  <h2 id="by-status" className="mb-3 text-sm font-semibold text-content">
                    {t("resumen.byStatus")}
                  </h2>
                  {statusRows.length === 0 ? (
                    <p className="text-sm text-content-muted">—</p>
                  ) : (
                    <ul className="space-y-2">
                      {statusRows.map((r) => (
                        <li key={r.status} className="text-xs">
                          <div className="mb-0.5 flex items-center justify-between">
                            <span className="text-content-secondary">{t(`status.${r.status}`)}</span>
                            <span className="tabular-nums text-content">{r.n}</span>
                          </div>
                          <div className="h-1.5 w-full overflow-hidden rounded-full bg-subtle">
                            <div className={cn("h-full rounded-full", TONE_BG[STATUS_TONES[r.status]])} style={{ width: `${r.pct}%` }} />
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="mt-3 text-[11px] text-content-muted">{t("resumen.kpi.documents", { n: d.documents_total })}</div>
                </section>
              </div>

              <section className="rounded-xl border border-line bg-card p-4" aria-labelledby="expiring-soon">
                <div className="mb-3 flex items-center justify-between">
                  <h2 id="expiring-soon" className="text-sm font-semibold text-content">
                    {t("resumen.expiringSoon")}
                  </h2>
                  <Button size="xs" variant="ghost" onClick={() => navigate("/documentacion/vencimientos")}>
                    {t("menu.vencimientos")} <i className="ri-arrow-right-line" />
                  </Button>
                </div>
                {d.expiring_soon.length === 0 ? (
                  <p className="text-sm text-content-muted">{t("resumen.expiringSoonEmpty")}</p>
                ) : (
                  <ul className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3">
                    {d.expiring_soon.map((row) => (
                      <li key={row.id}>
                        <button type="button" onClick={() => navigate(documentacionSubjectRoute(row.subject.kind, row.subject.id))} className="flex w-full items-center gap-3 rounded-lg border border-line px-3 py-2 text-left hover:bg-subtle">
                          <span className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-subtle", { neutral: "text-content-secondary", primary: "text-primary-500", success: "text-success", warning: "text-warning", error: "text-error", info: "text-info" }[expiryTone(row.expires_in_days)])}>
                            <i className="ri-calendar-event-line" />
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-sm text-content">{documentTypeName(row.doc_type, i18n.language, row.name)}</div>
                            <div className="truncate text-[11px] text-content-muted">
                              {t(`kind.${row.subject.kind}`)} {row.subject.id} · {shortDate(row.expires_at, i18n.language)}
                            </div>
                          </div>
                          <ExpiryChip days={row.expires_in_days} expiresAt={row.expires_at} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>
          ) : null}
        </LoadBlock>
      </div>
    </div>
  );
}
